import noop from 'lodash/fp/noop'
import { useFilterSilentSearchErrors } from 'app/search/hooks/useFilterSilentSearchErrors'
import { formatSearchErrors, formatSearchResults } from 'core/vehicles/mappings/vehicleSearchFormat'
import { useMemo } from 'react'
import { ResultObjectType } from '@cbgms/api/modules/enums'
import { IVehicleSearchFormatResults, QuotationResultFields } from 'core/vehicles/mappings/vehicle'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import { usePrimaryActionBuilder } from 'app/menu/components/Search/hooks/use-primary-action-builder'
import {
  IAccount,
  IInvoice,
  IOrder,
  IPopularSearchAttributesValue,
  IProduct,
  ISearchResponse,
  ISearchResultAccount,
  IVehicleType,
  IWorkorder,
  RecentlyOpenedEntity
} from '@cbgms/api'
import { IGroup } from '@cbgms/base/utils/helpers'
import { IQuotation as WorkorderQuotation } from '@cbgms/api/modules/workorder-quotations/models'
import { IQuotation as VehicleSalesQuotation } from '@cbgms/api/modules/inventory-vehicles/quotation_models'
import { TopSearchResultType, VirtualSearchResultType } from '@cbgms/components/src/top-search/search-v1/Results/TopSearchResultType'
import { ITopSearchResultProps } from '@cbgms/components/src/top-search/search-v1/Results/ResultComponentSelector'
import { useFeature } from '@cbgms/base/feature-flags'
export interface ITitleTranslationProvider {
  Type: TopSearchResultType
}

const DirectMatchesSearchScoreCutoff = 19999 // Backend will deliver search scores, direct matches start at 20k+ score

function getTitleTranslationKey(isPortal: boolean, val: ITitleTranslationProvider, hasB2BSearchOptimization: boolean): string | undefined {
  let resultType = val.Type
  if (resultType === VirtualSearchResultType.CallToActionViewMoreSupplierOrder) {
    resultType = ResultObjectType.Order // rename and group CTA with orders
  }
  if (resultType === ResultObjectType.Error) {
    return undefined
  }
  if (hasB2BSearchOptimization && resultType === ResultObjectType.Parts) {
    return undefined
  }

  const suffix = isPortal && (resultType === ResultObjectType.WorkOrder || resultType === ResultObjectType.GenericVehicle) ? '_portal' : ''

  const typeTranslation = (Object.values(ResultObjectType) as string[]).includes(resultType.toString())
    ? `Enum.EnumResultObjectType_${resultType}${suffix}`
    : `DashboardSearch.AccountType_${resultType}`

  return typeTranslation
}

export interface ITopSearchFormatResults {
  Score: number
  Type: TopSearchResultType
  Vehicle?: IGroup<IVehicleType>
  Account?: ISearchResultAccount
  WorkorderInvoice?: Partial<IInvoice>
  WorkorderQuotation?: QuotationResultFields & WorkorderQuotation
  VehicleSalesQuotation?: QuotationResultFields & VehicleSalesQuotation
  Workorder?: QuotationResultFields & IWorkorder
  Order?: IOrder & Pick<IAccount, 'CompanyName'>
  Product?: IProduct
  ProductCount?: number
  TyreSize?: IPopularSearchAttributesValue
  Errors?: string[]
  RecentlyOpened?: RecentlyOpenedEntity
}

/**
 * formatSearchForTopSearch will transform to fit the top search.
 * Top search will not list out all external parts, instead it will render a link to the dashboard-search part tab
 */
export const formatSearchForTopSearch = (searchData: IVehicleSearchFormatResults[]): ITopSearchFormatResults[] => {
  const data = searchData || []
  // we need to do some fancy tricks with vehicle results if multiple are returned (ie multiple hbase)
  const untransformedResults = data.filter(result => result.Type !== ResultObjectType.Parts && result.Type !== ResultObjectType.Order)

  const resultOrders = data.filter(result => result.Type === ResultObjectType.Order)
  const orderItems = (resultOrders || []).reduce<ITopSearchFormatResults[]>((output, v, _, all) => {
    if (output.length >= 3) {
      // only show 3 order results, if there's more, show a 'more results' option
      return output
    }
    if (output.length === 2 && all.length > 3) {
      // at exactly 3 results, no need to show 'more option, anything more then that and we will show 'more' option
      output.push({ ...v, Type: VirtualSearchResultType.CallToActionViewMoreSupplierOrder })
    } else {
      output.push(v)
    }
    return output
  }, [])
  return [...untransformedResults, ...orderItems].sort((a, b) => b.Score - a.Score)
}

export const useMappedSearchResults = (
  searchResponse: ISearchResponse | undefined,
  searchQuery: string,
  searchTypes: ResultObjectType[],
  clearQuery: () => void,
  TopSearchResult: (_: ITopSearchResultProps) => JSX.Element | null,
  overrideB2BSearchOptimization?: boolean
) => {
  const isPortal = useIsPortal()
  const actionBuilder = usePrimaryActionBuilder({ clearQuery })
  const featureFlagValue = useFeature('b2b-search-optimization')
  const hasB2BSearchOptimization = overrideB2BSearchOptimization !== undefined ? overrideB2BSearchOptimization : featureFlagValue
  const mappedResults: ITopSearchFormatResults[] = useMemo(() => {
    // group and convert (generic-)vehicle results
    const searchResults = formatSearchResults(searchResponse)
    const searchResultsForTopSearch = formatSearchForTopSearch(searchResults)
    // handle errors, show/hide errors for search results
    const mappedErrors = formatSearchErrors(searchResponse, useFilterSilentSearchErrors)
    return [...searchResultsForTopSearch, ...mappedErrors]
  }, [searchResponse])

  const results: ITopSearchResultProps[] = mappedResults.map((val, index) => ({
    title: index.toString(),
    Title: getTitleTranslationKey(isPortal, val, hasB2BSearchOptimization),
    as: TopSearchResult,
    query: searchQuery,
    clearQuery,
    hasGroupHeader: true, // Titles are rendered as groups with titles
    primaryAction: actionBuilder(val.Type, searchQuery, val),
    ...val
  }))

  // Add a 'search-for-products' result for portal users. remove this if condition while cleaning up feature flag
  if (searchQuery && isPortal && searchTypes.includes(ResultObjectType.Parts) && !hasB2BSearchOptimization) {
    let insertIndex = results.findIndex(r => r.Score <= DirectMatchesSearchScoreCutoff)
    if (insertIndex === -1) {
      insertIndex = results.length
    }
    results.splice(insertIndex, 0, {
      Score: DirectMatchesSearchScoreCutoff,
      query: searchQuery,
      Type: VirtualSearchResultType.CallToActionSearchParts,
      clearQuery,
      hasGroupHeader: true,
      primaryAction: actionBuilder(VirtualSearchResultType.CallToActionSearchParts, searchQuery, null),
      Title: getTitleTranslationKey(isPortal, { Type: ResultObjectType.Parts }, hasB2BSearchOptimization)
    })
  }

  // insert a 'create-vehicle-manually-mmt' result directly after the last genericVehicle result, otherwise append at the end.
  if (searchQuery && searchTypes.includes(ResultObjectType.GenericVehicle)) {
    let insertIndex = results.findLastIndex(r => r.Type === ResultObjectType.GenericVehicle) + 1
    if (insertIndex === 0) {
      insertIndex = results.length
    }
    results.splice(insertIndex, 0, {
      Score: (results[insertIndex - 1]?.Score || 0) - 1,
      query: searchQuery,
      Type: VirtualSearchResultType.CallToActionCreateVehicleManually,
      clearQuery,
      hasGroupHeader: true,
      primaryAction: actionBuilder(VirtualSearchResultType.CallToActionCreateVehicleManually, searchQuery, null),
      Title: getTitleTranslationKey(isPortal, { Type: ResultObjectType.GenericVehicle }, hasB2BSearchOptimization)
    })
  }

  // show no results msg if there are no actual results
  if (results.length === 0) {
    results.push({
      Score: 0,
      query: searchQuery,
      Type: VirtualSearchResultType.NoResults,
      clearQuery: noop,
      hasGroupHeader: false,
      primaryAction: actionBuilder(VirtualSearchResultType.NoResults, searchQuery, null)
    })
  }

  return results
}
